import React, { useState, useEffect, useRef } from 'react';
import { 
  ensureAudioResumed,
  createRainSound,
  createThunderstormRainSound,
  createSnowWindSound,
  createCloudWindSound,
  createCloudMusic,
  stopCloudMusic,
  playSoothingThunder
} from './audioUtils';

const WeatherWindow = ({ audioCtxRef, soundEnabled }) => {
  const canvasRef = useRef(null);
  const [weather, setWeather] = useState('rain');
  const particlesRef = useRef([]);
  const audioRef = useRef(null);
  const musicRef = useRef(null);
  const thunderTimeoutRef = useRef(null);
  const lightningRef = useRef({ active: false, opacity: 0 });
  const timeRef = useRef(0);
  
  // Handle audio based on weather type
  useEffect(() => {
    const audioCtx = audioCtxRef?.current;
    if (!soundEnabled || !audioCtx) return;
    
    let isMounted = true;
    let localAudioRef = null;
    let localThunderInterval = null;
    
    // Clean up previous audio - with robust null checking
    const cleanupAudio = () => {
      if (audioRef.current && audioRef.current.gainNode && audioCtx) {
        try {
          audioRef.current.gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.5);
          const prevSource = audioRef.current.source;
          setTimeout(() => {
            try { prevSource?.stop(); } catch { /* ignore */ }
          }, 600);
          audioRef.current = null;
        } catch { /* ignore cleanup errors */ }
      }
      if (thunderTimeoutRef.current) {
        clearInterval(thunderTimeoutRef.current);
        thunderTimeoutRef.current = null;
      }
    };
    
    cleanupAudio();
    
    // Start new audio based on weather (async to handle AudioContext resume)
    const startAudio = async () => {
      if (!isMounted || !audioCtx) return;
      
      // Resume AudioContext if suspended (required by browsers)
      const isReady = await ensureAudioResumed(audioCtx);
      if (!isReady || !isMounted) return;
      
      try {
        if (weather === 'rain') {
          localAudioRef = createRainSound(audioCtx);
          audioRef.current = localAudioRef;
        } else if (weather === 'thunderstorm') {
          localAudioRef = createThunderstormRainSound(audioCtx);
          audioRef.current = localAudioRef;
          
          // Play initial POWERFUL thunder immediately
          setTimeout(() => {
            if (isMounted) playSoothingThunder(audioCtx, 0.9); // Loud initial rumble!
          }, 300);
          
          // Thunder rumbles every 4-7 seconds with dramatic lightning
          localThunderInterval = setInterval(() => {
            if (!isMounted) return;
            
            // DUCK THE RAIN - reduce rain volume so thunder is clearly heard
            if (audioRef.current && audioRef.current.gainNode) {
              audioRef.current.gainNode.gain.linearRampToValueAtTime(0.08, audioCtx.currentTime + 0.1);
            }
            
            // LIGHTNING FLASH FIRST - then thunder immediately after
            lightningRef.current = { active: true, opacity: 1.0 };
            
            // Play LOUD thunder WITH the lightning flash
            setTimeout(() => {
              if (!isMounted) return;
              playSoothingThunder(audioCtx, 1.0); // FULL VOLUME thunder!
            }, 50);
            
            // Lightning sequence - multiple flashes
            setTimeout(() => {
              if (!isMounted) return;
              lightningRef.current = { active: true, opacity: 0.3 };
              setTimeout(() => {
                if (!isMounted) return;
                lightningRef.current = { active: true, opacity: 0.9 };
                setTimeout(() => {
                  if (!isMounted) return;
                  lightningRef.current = { active: true, opacity: 0.2 };
                  setTimeout(() => {
                    lightningRef.current = { active: false, opacity: 0 };
                  }, 200);
                }, 120);
              }, 100);
            }, 80);
            
            // RESTORE rain volume after thunder fades (after ~4 seconds)
            setTimeout(() => {
              if (!isMounted) return;
              if (audioRef.current && audioRef.current.gainNode) {
                audioRef.current.gainNode.gain.linearRampToValueAtTime(0.3, audioCtx.currentTime + 1.5);
              }
            }, 3500);
            
          }, 6000 + Math.random() * 5000); // Every 6-11 seconds
          thunderTimeoutRef.current = localThunderInterval;
          
        } else if (weather === 'snow') {
          localAudioRef = createSnowWindSound(audioCtx);
          audioRef.current = localAudioRef;
        } else if (weather === 'clouds') {
          localAudioRef = createCloudWindSound(audioCtx);
          audioRef.current = localAudioRef;
          // Also start dreamy music for clouds
          musicRef.current = createCloudMusic(audioCtx);
        }
      } catch (e) { 
        console.warn('Audio init error:', e); 
      }
    };
    
    // Small delay to allow component to fully mount
    const startTimer = setTimeout(startAudio, 150);
    
    return () => {
      isMounted = false;
      clearTimeout(startTimer);
      // Stop cloud music if it was playing
      if (musicRef.current) {
        stopCloudMusic(musicRef.current, audioCtx);
        musicRef.current = null;
      }
      if (localAudioRef && localAudioRef.gainNode && audioCtx) {
        try {
          localAudioRef.gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.3);
          const srcToStop = localAudioRef.source;
          setTimeout(() => {
            try { srcToStop?.stop(); } catch { /* ignore */ }
          }, 400);
        } catch { /* ignore */ }
      }
      if (localThunderInterval) {
        clearInterval(localThunderInterval);
      }
      if (thunderTimeoutRef.current) {
        clearInterval(thunderTimeoutRef.current);
        thunderTimeoutRef.current = null;
      }
    };
  }, [weather, soundEnabled, audioCtxRef]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    
    let isCancelled = false;
    
    const initParticles = () => {
      particlesRef.current = [];
      const count = (weather === 'rain' || weather === 'thunderstorm') ? 250 : weather === 'snow' ? 120 : 6;
      
      for (let i = 0; i < count; i++) {
        if (weather === 'clouds') {
          // ELONGATED cotton ball clouds - WIDER than tall (like real cumulus)
          const puffCount = 10 + Math.floor(Math.random() * 5);
          const puffs = [];
          
          // Create puffs spread horizontally (wider than tall)
          for (let p = 0; p < puffCount; p++) {
            const horizontalSpread = 2.5; // Make clouds 2.5x wider than tall
            puffs.push({
              // Spread more horizontally
              x: (Math.random() - 0.5) * 100 * horizontalSpread,
              y: (Math.random() - 0.5) * 40,
              size: 0.4 + Math.random() * 0.5,
              brightness: 0.93 + Math.random() * 0.07,
            });
          }
          // Add more puffs on the sides for elongated shape
          for (let side = 0; side < 6; side++) {
            const xOffset = (side < 3 ? -1 : 1) * (60 + Math.random() * 50);
            puffs.push({
              x: xOffset,
              y: (Math.random() - 0.5) * 25,
              size: 0.35 + Math.random() * 0.35,
              brightness: 0.92 + Math.random() * 0.08,
            });
          }
          
          particlesRef.current.push({
            x: -300 + Math.random() * (canvas.width + 600),
            y: 100 + Math.random() * (canvas.height * 0.35),
            speed: 0.2 + Math.random() * 0.3,
            size: 60 + Math.random() * 50, // Base size
            opacity: 0.92 + Math.random() * 0.08,
            puffs: puffs,
            depth: 0.5 + Math.random() * 0.5,
          });
        } else {
          particlesRef.current.push({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            speed: (weather === 'rain' || weather === 'thunderstorm') ? 5 + Math.random() * 4 : 0.3 + Math.random() * 0.5,
            size: (weather === 'rain' || weather === 'thunderstorm') ? 2 : 6 + Math.random() * 6,
            opacity: 0.3 + Math.random() * 0.5,
            drift: (Math.random() * 2 - 1) * ((weather === 'thunderstorm') ? 1.5 : 0.5),
            length: (weather === 'rain' || weather === 'thunderstorm') ? 30 + Math.random() * 20 : 0,
          });
        }
      }
    };
    
    initParticles();
    
    // Draw SHARP, DEFINED cotton clouds - like real cumulus clouds
    const drawCottonBallCloud = (ctx, cloud, time) => {
      if (!cloud || !Array.isArray(cloud.puffs) || cloud.puffs.length === 0) return;
      
      try {
        ctx.save();
        
        // Sort puffs so larger ones draw first (back to front)
        const sortedPuffs = [...cloud.puffs].sort((a, b) => a.size - b.size);
        
        // Draw each puff with SHARP edges
        for (let i = 0; i < sortedPuffs.length; i++) {
          const puff = sortedPuffs[i];
          if (!puff) continue;
          
          // Gentle floating motion
          const wobbleX = Math.sin(time * 0.0008 + i * 0.5) * 2;
          const wobbleY = Math.cos(time * 0.0006 + i * 0.3) * 1;
          
          const x = cloud.x + puff.x + wobbleX;
          const y = cloud.y + puff.y + wobbleY;
          const size = cloud.size * puff.size;
          
          // SHARP gradient - quick falloff for defined edges
          const gradient = ctx.createRadialGradient(
            x - size * 0.25, y - size * 0.25, 0,  // Light source offset
            x, y, size
          );
          
          // Sharp white cloud with defined edge
          gradient.addColorStop(0, `rgba(255, 255, 255, ${cloud.opacity})`);
          gradient.addColorStop(0.4, `rgba(255, 255, 255, ${cloud.opacity * 0.95})`);
          gradient.addColorStop(0.7, `rgba(252, 253, 255, ${cloud.opacity * 0.85})`);
          gradient.addColorStop(0.88, `rgba(245, 248, 252, ${cloud.opacity * 0.5})`);
          gradient.addColorStop(0.95, `rgba(240, 245, 250, ${cloud.opacity * 0.15})`);
          gradient.addColorStop(1, 'rgba(235, 242, 248, 0)');
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fill();
        }
        
        // Add bright highlight spots for dimension (like in real clouds)
        for (let i = 0; i < 3; i++) {
          const puff = sortedPuffs[Math.floor(sortedPuffs.length * 0.7) + i];
          if (!puff) continue;
          
          const x = cloud.x + puff.x - cloud.size * puff.size * 0.2;
          const y = cloud.y + puff.y - cloud.size * puff.size * 0.25;
          const highlightSize = cloud.size * puff.size * 0.4;
          
          const highlight = ctx.createRadialGradient(x, y, 0, x, y, highlightSize);
          highlight.addColorStop(0, `rgba(255, 255, 255, ${cloud.opacity * 0.6})`);
          highlight.addColorStop(0.5, `rgba(255, 255, 255, ${cloud.opacity * 0.2})`);
          highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
          
          ctx.fillStyle = highlight;
          ctx.beginPath();
          ctx.arc(x, y, highlightSize, 0, Math.PI * 2);
          ctx.fill();
        }
        
        ctx.restore();
      } catch (e) {
        // Silently ignore cloud drawing errors
      }
    };
    
    const animate = () => {
      if (isCancelled) return;
      
      try {
        timeRef.current++;
      
      // Sky gradients based on weather
      let skyGradient;
      if (weather === 'thunderstorm') {
        skyGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        skyGradient.addColorStop(0, '#1A2530');
        skyGradient.addColorStop(0.3, '#2A3545');
        skyGradient.addColorStop(0.7, '#3A4555');
        skyGradient.addColorStop(1, '#4A5565');
      } else if (weather === 'rain') {
        skyGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        skyGradient.addColorStop(0, '#3D4F5F');
        skyGradient.addColorStop(0.3, '#4A5D6D');
        skyGradient.addColorStop(0.7, '#5A6B7A');
        skyGradient.addColorStop(1, '#6B7C8B');
      } else if (weather === 'snow') {
        skyGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        skyGradient.addColorStop(0, '#8A9CAD');
        skyGradient.addColorStop(0.5, '#A5B5C5');
        skyGradient.addColorStop(1, '#C5D5E5');
      } else {
        // Clean, bright blue sky for fluffy cotton ball clouds
        skyGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        skyGradient.addColorStop(0, '#4A90D9');    // Nice blue at top
        skyGradient.addColorStop(0.4, '#6AB0E8');  // Brighter mid
        skyGradient.addColorStop(0.7, '#8AC8F0');  // Lighter
        skyGradient.addColorStop(1, '#B0DCF8');    // Pale horizon
      }
      
      ctx.fillStyle = skyGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // DRAMATIC Lightning flash for thunderstorm - brighter, more impactful
      if (weather === 'thunderstorm' && lightningRef.current.active && lightningRef.current.opacity > 0) {
        // Main flash - bright white/yellow
        ctx.fillStyle = `rgba(255, 255, 240, ${lightningRef.current.opacity * 0.9})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Add purple/blue tinge at edges for realism
        const flashGrad = ctx.createRadialGradient(
          canvas.width / 2, canvas.height / 3, 0,
          canvas.width / 2, canvas.height / 2, canvas.width
        );
        flashGrad.addColorStop(0, `rgba(255, 255, 255, ${lightningRef.current.opacity * 0.6})`);
        flashGrad.addColorStop(0.5, `rgba(200, 180, 255, ${lightningRef.current.opacity * 0.3})`);
        flashGrad.addColorStop(1, `rgba(100, 80, 180, ${lightningRef.current.opacity * 0.1})`);
        ctx.fillStyle = flashGrad;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw actual lightning bolt when flash is bright
        if (lightningRef.current.opacity > 0.5) {
          ctx.strokeStyle = `rgba(255, 255, 255, ${lightningRef.current.opacity})`;
          ctx.lineWidth = 3;
          ctx.shadowColor = '#FFF';
          ctx.shadowBlur = 20;
          
          // Jagged lightning bolt
          const startX = canvas.width * (0.3 + Math.random() * 0.4);
          let x = startX;
          let y = 0;
          ctx.beginPath();
          ctx.moveTo(x, y);
          
          while (y < canvas.height * 0.6) {
            x += (Math.random() - 0.5) * 80;
            y += 20 + Math.random() * 40;
            ctx.lineTo(x, y);
            
            // Occasional branch
            if (Math.random() > 0.7) {
              const branchX = x + (Math.random() - 0.5) * 100;
              const branchY = y + 30 + Math.random() * 50;
              ctx.moveTo(x, y);
              ctx.lineTo(branchX, branchY);
              ctx.moveTo(x, y);
            }
          }
          ctx.stroke();
          ctx.shadowBlur = 0;
        }
        
        // Decay the flash
        lightningRef.current.opacity *= 0.88;
        if (lightningRef.current.opacity < 0.05) {
          lightningRef.current.active = false;
          lightningRef.current.opacity = 0;
        }
      }
      
      particlesRef.current.forEach(p => {
        if (weather === 'rain' || weather === 'thunderstorm') {
          // Rain streaks
          const gradient = ctx.createLinearGradient(p.x, p.y, p.x + p.drift, p.y + p.length);
          gradient.addColorStop(0, `rgba(180, 200, 220, 0)`);
          gradient.addColorStop(0.3, `rgba(200, 220, 240, ${p.opacity * 0.5})`);
          gradient.addColorStop(1, `rgba(220, 235, 250, ${p.opacity * 0.8})`);
          
          ctx.strokeStyle = gradient;
          ctx.lineWidth = weather === 'thunderstorm' ? 2 : 1.5;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + p.drift * 2, p.y + p.length);
          ctx.stroke();
          
          p.y += p.speed;
          p.x += p.drift * 0.3;
        } else if (weather === 'snow') {
          // Soft snowflakes with gentle glow
          const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${p.opacity})`);
          gradient.addColorStop(0.5, `rgba(255, 255, 255, ${p.opacity * 0.5})`);
          gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
          
          p.y += p.speed;
          p.x += Math.sin(p.y * 0.006 + p.opacity * 10) * 0.3;
        } else {
          // Clouds - fluffy cotton ball clouds floating by
          drawCottonBallCloud(ctx, p, timeRef.current);
          
          // Move clouds - parallax effect (further clouds move slower)
          const depth = p.depth || 1;
          p.x += p.speed * depth;
          
          // Gentle vertical drift
          p.y += Math.sin(timeRef.current * 0.002 + p.x * 0.001) * 0.12;
        }
        
        // Reset particles that go off screen
        if (p.y > canvas.height + 40) { p.y = -40; p.x = Math.random() * canvas.width; }
        if (p.x > canvas.width + 400) { 
          p.x = -350 - Math.random() * 100; // Reset further off-screen for wider clouds
          p.y = 100 + Math.random() * (canvas.height * 0.35);
        }
      });
      
      // Window frame
      ctx.strokeStyle = '#8B7355';
      ctx.lineWidth = 24;
      ctx.strokeRect(0, 0, canvas.width, canvas.height);
      
      ctx.strokeStyle = '#A08060';
      ctx.lineWidth = 4;
      ctx.strokeRect(12, 12, canvas.width - 24, canvas.height - 24);
      
      } catch (e) {
        // Silently handle animation errors to prevent crash overlays
        console.warn('Animation frame error:', e);
      }
      
      if (!isCancelled) {
        requestAnimationFrame(animate);
      }
    };
    
    const animationId = requestAnimationFrame(animate);
    return () => {
      isCancelled = true;
      cancelAnimationFrame(animationId);
    };
  }, [weather]);
  
  return (
    <div className="flex flex-col items-center justify-center h-full gap-6">
      <div className="relative rounded-xl overflow-hidden shadow-2xl" style={{ width: '85vw', maxWidth: '1000px', height: '65vh', maxHeight: '600px' }}>
        <canvas ref={canvasRef} className="w-full h-full" />
      </div>
      <div className="flex gap-3 flex-wrap justify-center">
        {['rain', 'thunderstorm', 'snow', 'clouds'].map(w => (
          <button
            key={w}
            onClick={() => setWeather(w)}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${weather === w ? 'bg-white/30 text-white shadow-lg' : 'text-white/60 hover:text-white/80 hover:bg-white/10'}`}
          >
            {w === 'rain' ? '🌧️ Rain' : w === 'thunderstorm' ? '⛈️ Thunderstorm' : w === 'snow' ? '❄️ Snow' : '🎨 Clouds'}
          </button>
        ))}
      </div>
    </div>
  );
};

export default WeatherWindow;
